import { Injectable } from '@angular/core';
import { Observable, Subject } from 'rxjs';
import { filter, map } from 'rxjs/operators';
import { App } from '@df/core/app';

/**
 * Event interface.
 *
 * @export
 * @interface IEvent
 */
export interface IEvent<T = any> {
    name: string;
    data?: T;
}

/**
 * Communication between components.
 *
 * Access it either directly importing or via app.event.
 *
 * ```typescript
 *
 * export class Component implements OnInit, OnDestroy {
 *   subscription: any;
 *
 *   ngOnInit() {
 *     this.subscription = app.events.on('component.open').subscribe(data => {
 *       console.log(data);
 *     });
 *   }
 *
 *   ngOnDestroy() {
 *     this.subscriptions.unsubscribe();
 *   }
 *
 *   onClick() {
 *     app.events.broadcast('component.open', true);
 *   }
 * ```
 *
 *
 * @deprecated Use DfEventService instead.
 *
 * @export
 * @class Event
 */
@Injectable({
    providedIn: 'root'
})
export class Events {
    public readonly event$ = new Subject<IEvent>();

    constructor(app: App) {
        app.events = this;
    }

    /**
     * Publish event with optional data.
     *
     * ``` typescript
     * app.event.broadcast('event-name');
     * app.event.broadcast('event-name.data', { data: true });
     * ```
     *
     * @param {string} name
     * @param {*} [data]
     * @memberof Event
     */
    broadcast<T = any>(name: string, data?: T): void {
        this.event$.next({ name, data });
    }

    /**
     * Watch for event.
     *
     * ```typescript
     * app.event.on('event-name').subscribe(data => console.log(data));
     * ```
     *
     * @param {string} name Event name.
     * @returns {Observable<any>}
     * @memberof Event
     */
    on<T = any>(name: string): Observable<T> {
        return this.event$.asObservable().pipe(
            filter(item => item.name === name),
            map(item => item.data)
        );
    }
}

declare module '@df/core/app' {
    interface App {
        events: Events;
    }
}
